/**
 * contentValidators/explanationChecks.ts
 * Validates explanation quality: min length, generic text, forbidden openers,
 * didactic depth, jargon, passive voice, sentence length, rule references,
 * and check strategy requirements.
 */

import type { ValidationIssue } from '../../issueContract'
import { coach, info, blocker } from '../../issueContract'
import {
  getTierBand,
  getMinExplanationLength,
  getDistractorPattern,
  requiresRuleReference,
  getCheckStrategyMinTier,
} from '../../policyEngine'
import {
  isGenericText,
  isDidactic,
  GENERIC_EXPLANATION_PATTERNS,
  FORBIDDEN_OPENER_PATTERNS,
  CHECK_STRATEGY_PATTERN,
  TOPO_GEOGRAPHIC_DEPTH_PATTERN,
  detectJargon,
  hasPassiveVoice,
  detectLongSentences,
  getMaxWordsPerSentence,
} from '../../../utils'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

/**
 * Validate explanation quality for a question.
 */
export function validateExplanationQuality(
  question: Question,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const tier = question.tier ?? 1
  const module = question.module

  const explanation = question.feedback?.explanationText?.trim() ?? ''
  if (!explanation) return issues

  // Min length check
  const minExplanation = getMinExplanationLength(tier)
  if (explanation.length < minExplanation) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_EXPLANATION_TOO_SHORT',
        `Explanation (${explanation.length} chars) below minimum ${minExplanation} for tier ${tier}`,
        {
          field: 'feedback.explanationText',
          expected: `≥${minExplanation}`,
          actual: String(explanation.length),
          questionId: qId,
          familyId: fId,
          severity: tier >= 7 ? SEVERITY.NEAR_HIGH : SEVERITY.NEAR_REVIEW,
        },
      ),
    )
  }

  // Generic check
  if (isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS)) {
    issues.push(
      blocker(
        'content',
        STAGE,
        'CONTENT_GENERIC_EXPLANATION',
        'Explanation is generic/boilerplate — must explain the concept or calculation',
        {
          field: 'feedback.explanationText',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.HIGH,
        },
      ),
    )
  }

  // ── Forbidden opener check ──────────────────────────────────────────────
  {
    for (const { pattern, label } of FORBIDDEN_OPENER_PATTERNS) {
      if (pattern.test(explanation)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_FORBIDDEN_OPENER',
            `Explanation starts with forbidden opener "${label}" — rewrite to integrate naturally`,
            {
              field: 'feedback.explanationText',
              actual: label,
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
              suggestion: `Remove the "${label}" opener and integrate the content naturally into the explanation`,
            },
          ),
        )
      }
    }
  }

  // Didactic depth check (only if not generic)
  // TOPO EXEMPTION: Topo is knowledge-recall. If the explanation references
  // geographic facts (hasModuleSpecificDepth), that IS didactic for Topo.
  if (
    !isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS) &&
    explanation.length >= 30
  ) {
    const isTopoWithGeographicDepth =
      module === 'Topo' && TOPO_GEOGRAPHIC_DEPTH_PATTERN.test(explanation)
    if (!isDidactic(explanation) && !isTopoWithGeographicDepth) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_EXPLANATION_NOT_DIDACTIC',
          'Explanation lacks didactic depth — should show steps, reference rules, or explain reasoning',
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MEDIUM,
          },
        ),
      )
    }
  }

  // ── Jargon detection ────────────────────────────────────────────────────
  const jargonFound = detectJargon(explanation, tier)
  for (const { term, replacement } of jargonFound) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_JARGON_DETECTED',
        `Jargon "${term}" in explanation — too complex for children aged 9-11`,
        {
          field: 'feedback.explanationText',
          actual: term,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
          suggestion: `Replace "${term}" with ${replacement}`,
        },
      ),
    )
  }

  // ── Passive voice detection ─────────────────────────────────────────────
  if (hasPassiveVoice(explanation)) {
    issues.push(
      info(
        'content',
        STAGE,
        'CONTENT_EXPLANATION_PASSIVE',
        'Explanation uses passive voice — use active voice ("kijk", "tel", "schrijf")',
        {
          field: 'feedback.explanationText',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.COSMETIC,
          suggestion: 'Replace passive constructions with active verbs',
        },
      ),
    )
  }

  // ── Sentence length check ───────────────────────────────────────────────
  const longSentences = detectLongSentences(explanation, tier)
  if (longSentences.length > 0) {
    const maxWords = getMaxWordsPerSentence(tier)
    issues.push(
      info(
        'content',
        STAGE,
        'CONTENT_SENTENCE_TOO_LONG',
        `${longSentences.length} sentence(s) exceed the ${maxWords}-word limit for tier ${tier}`,
        {
          field: 'feedback.explanationText',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_MINOR,
          suggestion: `Keep sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`,
        },
      ),
    )
  }

  // Module-specific rule reference
  if (module && requiresRuleReference(module)) {
    const pattern = getDistractorPattern(module)
    if (pattern && !pattern.test(explanation) && !isDidactic(explanation)) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_MISSING_RULE_REF',
          `Explanation should reference module-specific rules for ${module}`,
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MINOR,
          },
        ),
      )
    }
  }

  // Check strategy requirement (SWW tier 7+)
  if (module) {
    const checkMinTier = getCheckStrategyMinTier(module)
    if (checkMinTier !== null && tier >= checkMinTier) {
      if (!CHECK_STRATEGY_PATTERN.test(explanation)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_MISSING_CHECK_STRATEGY',
            `Explanation for ${module} tier ≥${checkMinTier} should include a check strategy`,
            {
              field: 'feedback.explanationText',
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
            },
          ),
        )
      }
    }
  }

  return issues
}
